/**
 * What moved since this person last looked at their own list.
 *
 * A reply nobody notices is a reply that was not worth writing, and the
 * whole reason the lifecycle is visible to submitters is so they bother
 * submitting a second time. This marks the items whose state or reply has
 * changed since they were last seen.
 *
 * Seen state lives in the browser, so it does not follow someone to another
 * machine: they will see one stale badge there and nothing worse. A
 * server-side read flag is the right long-term home and is in the backend
 * requirements doc.
 *
 * Pure, apart from the storage object it is handed.
 */

export const SEEN_KEY = 'feedback_seen';

const isBlank = (value) => value === null || value === undefined || String(value).trim() === '';
const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** The stamp that says "this is the version I have read". */
export function stampOf(item) {
  const row = item || {};
  return `${text(row.state)}|${text(row.updated_at || row.created_at)}|${text(row.reply).length}`;
}

function store(storage) {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (e) {
    // Safari in private mode throws on access, not on use.
    return null;
  }
}

/** Never throws, whatever is in the key -- a corrupt value reads as empty. */
export function readSeen(storage) {
  const box = store(storage);
  if (!box) return {};
  try {
    const parsed = JSON.parse(box.getItem(SEEN_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
}

/**
 * Record the current version of every item passed.
 *
 * Only ids still in the list are kept, so the key cannot grow without
 * limit as items are closed and fall out of view.
 */
export function markSeen(storage, items) {
  const box = store(storage);
  const list = Array.isArray(items) ? items : [];
  const next = list.reduce((acc, item) => {
    if (!item || isBlank(item.id)) return acc;
    return { ...acc, [String(item.id)]: stampOf(item) };
  }, {});

  if (box) {
    try {
      box.setItem(SEEN_KEY, JSON.stringify(next));
    } catch (e) {
      // A full or blocked store costs a badge, not the page.
    }
  }
  return next;
}

/**
 * Record one item without touching the rest.
 *
 * Used the moment someone submits: they wrote it, so it is read, and
 * rebuilding the whole map from a list we do not have would wipe every
 * other item's stamp and badge the entire page.
 */
export function markOneSeen(storage, item) {
  const box = store(storage);
  if (!item || isBlank(item.id)) return readSeen(storage);
  const next = { ...readSeen(storage), [String(item.id)]: stampOf(item) };
  if (box) {
    try {
      box.setItem(SEEN_KEY, JSON.stringify(next));
    } catch (e) {
      // A full or blocked store costs a badge, not the page.
    }
  }
  return next;
}

/** True when this item has changed since it was last read. */
export function isUnseen(item, seen) {
  if (!item || isBlank(item.id)) return false;
  const record = (seen || {})[String(item.id)];
  if (!record) return true;
  return record !== stampOf(item);
}

/** The ids of everything that moved, for a count on the nav. */
export function unseenOf(items, seen) {
  const list = Array.isArray(items) ? items : [];
  return list.filter((item) => isUnseen(item, seen)).map((item) => String(item.id));
}
